// src/features/system/ai/hooks/useAiText.ts
// ────────────────────────────────────────────────────────────────────────────
// Hook-Owns-All-State for OpenAI text generation (via BFF /ai/text).
//
// Dual-mode: handles both 200 (sync inline) and 202 (async dispatch via SSE).
// Composable: used by workflow builder, handler catalog, and future AI panels.
// ────────────────────────────────────────────────────────────────────────────
import { useState, useCallback } from 'react';
import { useAsyncJob } from '@/hooks/useAsyncJob';
import {
  createOpenAITextApi,
  type CreateTextInput,
  type OpenAITextResponse,
} from '../api/openaiApi';

export type AiTextStatus = 'idle' | 'loading' | 'success' | 'error';

export function useAiText() {
  const asyncJob = useAsyncJob();

  const [status, setStatus] = useState<AiTextStatus>('idle');
  const [result, setResult] = useState<OpenAITextResponse | null>(null);
  const [error, setError]   = useState<string | null>(null);

  const generate = useCallback(async (input: CreateTextInput): Promise<OpenAITextResponse | undefined> => {
    setStatus('loading');
    setError(null);
    setResult(null);
    asyncJob.reset();

    try {
      const res = await createOpenAITextApi(input);

      // Dual-mode: detect 202 Accepted (async dispatch)
      if ((res as any).__httpStatus === 202 && (res as any).run_id) {
        asyncJob.start((res as any).run_id, (res as any).poll_url);
        return undefined; // status stays 'loading' until SSE resolves
      }

      // Legacy 200: inline result
      setResult(res);
      setStatus('success');
      return res;
    } catch (err: any) {
      setError(err?.message || 'AI text generation failed');
      setStatus('error');
      return undefined;
    }
  }, [asyncJob]);

  const reset = useCallback(() => {
    setStatus('idle');
    setResult(null);
    setError(null);
    asyncJob.reset();
  }, [asyncJob]);

  // ── Async result merge ──────────────────────────────────────────────
  const effectiveStatus = (() => {
    if (asyncJob.status === 'completed' && status === 'loading') return 'success' as AiTextStatus;
    if (asyncJob.status === 'failed'    && status === 'loading') return 'error'   as AiTextStatus;
    return status;
  })();

  const effectiveResult = (() => {
    if (asyncJob.status === 'completed' && asyncJob.result) {
      const out = asyncJob.result.outcome;
      return {
        ok:      true as const,
        model:   (out.model as string)   ?? '',
        content: (out.content as string) ?? '',
        usage:   out.usage as OpenAITextResponse['usage'],
      } satisfies OpenAITextResponse;
    }
    return result;
  })();

  const effectiveError = asyncJob.status === 'failed' ? asyncJob.error : error;

  return {
    status: effectiveStatus,
    result: effectiveResult,
    error: effectiveError,
    generate,
    reset,
    // Async job cancellation
    cancelAsync: asyncJob.cancel,

    // Async job state (available when 202 path is active)
    asyncJob: {
      status:   asyncJob.status,
      progress: asyncJob.progress,
      runId:    asyncJob.runId,
      elapsed:  asyncJob.elapsedMs,
    },
  };
}
